/**
 * @fileoverview Provides an AuthContext and AuthProvider component
 * for managing authentication state (token, userId, username) in a React application.
 * It handles initial user identification (creating a user if necessary)
 * and token retrieval, persisting state in localStorage.
 */
import { api } from "@/api"; // Added api import
import { MaintenanceError } from "@/api/errors";
import type { ReactNode } from "react";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

interface AuthContextType {
  isLoading: boolean;
  token: string | null;
  userId: string | null;
  username: string | null;
  setToken: (token: string | null) => void;
  setUserId: (userId: string | null) => void;
  setUsername: (username: string | null) => void;
  logout: () => void;
  isMaintenanceMode: boolean;
  maintenanceMessage?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Internal state, initializing from localStorage
  const [token, setTokenState] = useState<string | null>(() => localStorage.getItem("authToken"));
  const [userId, setUserIdState] = useState<string | null>(() => localStorage.getItem("userId"));
  const [username, setUsernameState] = useState<string | null>(() =>
    localStorage.getItem("username")
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isMaintenanceMode, setIsMaintenanceMode] = useState<boolean>(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState<string | undefined>();

  // Memoized setters that update state and localStorage
  const setToken = useCallback((newToken: string | null) => {
    setTokenState(newToken);
    if (newToken) {
      localStorage.setItem("authToken", newToken);
    } else {
      localStorage.removeItem("authToken");
    }
  }, []);

  const setUserId = useCallback((newUserId: string | null) => {
    setUserIdState(newUserId);
    if (newUserId) {
      localStorage.setItem("userId", newUserId);
    } else {
      localStorage.removeItem("userId");
    }
  }, []);

  const setUsername = useCallback((newUsername: string | null) => {
    setUsernameState(newUsername);
    if (newUsername) {
      localStorage.setItem("username", newUsername);
    } else {
      localStorage.removeItem("username");
    }
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUserId(null);
    setUsername(null);
    console.log("[AuthContext] User logged out.");
  }, [setToken, setUserId, setUsername]);

  const authenticateWithCredentials = useCallback(
    async (credentials: { userName?: string; telegramAuth?: string }) => {
      console.log("[AuthContext] Attempting authentication with credentials:", credentials);

      try {
        const authResponse = await api.v1.auth.login.$post({
          json: credentials
        });

        const result = await authResponse.json();

        if (authResponse.status !== 200) {
          if ("error" in result) {
            const errorMsg = result.error || `Login failed (status ${authResponse.status})`;
            console.error("[AuthContext] Login failed:", errorMsg);
            alert(`Authentication Error: ${errorMsg}`);
          } else {
            console.error("[AuthContext] Login failed with unknown error");
            alert(`Authentication Error: Login failed (status ${authResponse.status})`);
          }
          logout();
          return;
        }

        if ("token" in result && "user" in result) {
          console.log("[AuthContext] Obtained auth token and user data.");
          setToken(result.token);
          setUserId(result.user.id);
          setUsername(result.user.username);
          setIsLoading(false);
        } else {
          console.error("[AuthContext] Token or user data not found in response");
          alert("Authentication Error: Incomplete response from server.");
          logout();
          setIsLoading(false);
          return;
        }
      } catch (error) {
        console.error("[AuthContext] Authentication process error:", error);

        // Handle MaintenanceError
        if (error instanceof MaintenanceError) {
          setIsMaintenanceMode(true);
          setMaintenanceMessage(error.message);
          setIsLoading(false);
          return;
        }

        if (error instanceof Error) {
          alert(`Authentication Error: ${error.message}`);
        } else {
          alert("An unknown error occurred during authentication.");
        }
        logout();
        setIsLoading(false);
      }
    },
    [setToken, setUserId, setUsername, logout]
  );

  const getToken = useCallback(
    async (userId: string) => {
      await authenticateWithCredentials({ userName: userId });
    },
    [authenticateWithCredentials]
  );

  useEffect(() => {
    // Extract URL parameters for authentication
    const urlParams = new URLSearchParams(window.location.search);
    const hashString = decodeURIComponent(window.location.hash.substring(1));
    const hashParams = new URLSearchParams(hashString);

    // Check if we have Telegram parameters in the hash
    let tgWebAppData: string | null = null;

    // Look for individual Telegram parameters
    const telegramParams = ["query_id", "user", "auth_date", "signature", "hash"];
    const foundTelegramParams: string[] = [];

    console.log("[AuthContext] Available hash parameters:", Array.from(hashParams.keys()));
    console.log("[AuthContext] Raw hash:", window.location.hash);

    telegramParams.forEach((param) => {
      const value = hashParams.get(param);
      console.log(`[AuthContext] Checking ${param}:`, value);
      if (value) {
        foundTelegramParams.push(`${param}=${value}`);
      }
    });

    console.log("[AuthContext] Found Telegram params:", foundTelegramParams);

    // If we have Telegram parameters, extract the raw Telegram data
    if (foundTelegramParams.length > 0) {
      const rawHash = decodeURIComponent(window.location.hash.substring(1)); // Remove the # character and decode

      // Remove the tgWebAppData= prefix if present and extract the actual Telegram init data
      if (rawHash.startsWith("tgWebAppData=")) {
        tgWebAppData = rawHash.substring("tgWebAppData=".length);
      } else {
        tgWebAppData = rawHash;
      }
    }

    const userIdParam = urlParams.get("user");

    console.log("[AuthContext] URL parameters:", {
      hasTgWebAppData: !!tgWebAppData,
      userIdParam,
      tgWebAppDataLength: tgWebAppData?.length
    });

    // Telegram auth takes precedence
    if (tgWebAppData) {
      console.log("[AuthContext] Found Telegram auth data, authenticating with Telegram");
      authenticateWithCredentials({ telegramAuth: tgWebAppData });
      return;
    }

    if (userIdParam) {
      console.log(`[AuthContext] Found user parameter: ${userIdParam}`);
      authenticateWithCredentials({ userName: userIdParam });
      return;
    }

    // Fallback to localStorage-based auth if no URL parameters
    if (userId) {
      console.log(
        `[AuthContext] Found existing userId in localStorage: ${userId}. Validating token...`
      );
      if (!token) {
        console.log("[AuthContext] No token found, attempting to get a new one.");
        getToken(userId);
      } else {
        console.log("[AuthContext] Found existing token. Assuming valid for now.");
        setIsLoading(false);
      }
    } else {
      console.log(
        "[AuthContext] No authentication data found. User must provide URL parameters to authenticate."
      );
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contextValue = useMemo(
    () => ({
      isLoading, // Added isLoading
      token,
      userId,
      username,
      setToken,
      setUserId,
      setUsername,
      logout,
      isMaintenanceMode,
      maintenanceMessage
    }),
    [
      isLoading,
      token,
      userId,
      username,
      setToken,
      setUserId,
      setUsername,
      logout,
      isMaintenanceMode,
      maintenanceMessage
    ]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
